import { DataResult, WidgetSettingsType } from '@thingslog/repositories';
import { DataResultParseStrategy } from './strategies/DataResultParseStrategy';
import { RequestType } from '@thingslog/repositories';
import { ApiDeviceSensorDataResultParseStrategy } from './strategies/ApiDeviceSensorDataResultParseStrategy';
import {
  DataResultParsed,
  DataResultParseDataPoint,
  DataResultParseStream,
} from './DataResultParsed';
import { ApiDevicesBatteryDataResultParseStrategy } from './strategies/ApiDevicesBatteryDataResultParseStrategy';
import { ApiDevicesSignalDataResultParseStrategy } from './strategies/ApiDevicesSignalDataResultParseStrategy';
import { ApiDevicesTransmissionsDataResultParseStrategy } from './strategies/ApiDevicesTransmissionsDataResultParseStrategy';
import { ApiDeviceReadingDataResultParseStrategy } from './strategies/ApiDeviceReadingDataResultParseStrategy';
import { ApiAlarmsCountDataResultParseStrategy } from './strategies/ApiAlarmsCountDataResultParseStrategy';
import { ApiHistogramDataResultParseStrategy } from './strategies/ApiHistogramDataResultParseStrategy';

export class DataResultParser {
  private strategy: DataResultParseStrategy;

  public constructor(requestType: RequestType) {
    this.strategy = this.deriveStrategy(requestType);
  }

  public setStrategy(requestType: RequestType): void {
    this.strategy = this.deriveStrategy(requestType);
  }

  public parse(dataResult: DataResult, widgetType: WidgetSettingsType): DataResultParsed {
    return this.strategy.parse(dataResult, widgetType);
  }

  public merge(parsedData: DataResultParsed): { [key: string]: string | number }[] {
    const mergedDataMap = new Map<string, { [key: string]: string | number }>();
    const xAxisDataKey = parsedData.xAxis?.dataKey || 'date';

    parsedData.dataStreams.forEach((stream: DataResultParseStream, streamIdx: number) => {
      stream.visualization.forEach((viz: { dataKey: string }, vizIdx: number) => {
        const uniqueDataKey = `value_${streamIdx}_${vizIdx}`;
        const visualizationDataKeys = new Set<string>(
          stream.visualization.map((visualization: { dataKey: string }): string => {
            return visualization.dataKey;
          })
        );

        stream.data.forEach((dataPoint: DataResultParseDataPoint) => {
          const xAxisValue = dataPoint[xAxisDataKey] as string;
          if (!mergedDataMap.has(xAxisValue)) {
            mergedDataMap.set(xAxisValue, { [xAxisDataKey]: xAxisValue });
          }
          const mergedPoint = mergedDataMap.get(xAxisValue)!;
          Object.entries(dataPoint).forEach(([key, value]: [string, string | number]): void => {
            if (!visualizationDataKeys.has(key)) {
              mergedPoint[key] = value;
            }
          });
          const value = dataPoint[viz.dataKey];
          if (value !== undefined && value !== null) {
            // Ensure value is a number for Recharts
            mergedPoint[uniqueDataKey] = typeof value === 'string' ? parseFloat(value) || 0 : value;
          }
        });
      });
    });

    const mergedData = Array.from(mergedDataMap.values()).sort(
      (a: { [key: string]: string | number }, b: { [key: string]: string | number }) => {
        const valueA = a[xAxisDataKey] as string;
        const valueB = b[xAxisDataKey] as string;
        return valueA.localeCompare(valueB);
      }
    );

    return mergedData;
  }

  private deriveStrategy(RequestType: RequestType): DataResultParseStrategy {
    switch (RequestType) {
      case 'api-alarms-count':
        return new ApiAlarmsCountDataResultParseStrategy();
      case 'api-battery-histogram':
        return new ApiHistogramDataResultParseStrategy({ color: '#22c55e' });
      case 'api-device-sensor':
        return new ApiDeviceSensorDataResultParseStrategy();
      case 'api-devices-battery':
        return new ApiDevicesBatteryDataResultParseStrategy();
      case 'api-devices-signal':
        return new ApiDevicesSignalDataResultParseStrategy();
      case 'api-devices-transmissions':
        return new ApiDevicesTransmissionsDataResultParseStrategy();
      case 'api-signal-histogram':
        return new ApiHistogramDataResultParseStrategy({ color: '#60a5fa' });
      case 'api-readings-device-groups':
        return new ApiDeviceReadingDataResultParseStrategy();
      case 'api-readings-device-sensor-list':
        return new ApiDeviceReadingDataResultParseStrategy();
      case 'api-readings-device-sensor-type-list':
        return new ApiDeviceReadingDataResultParseStrategy();
      case 'api-readings-mixed':
        return new ApiDeviceReadingDataResultParseStrategy();
      default:
        throw new Error('Unsupported request type');
    }
  }
}
